import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, Tab, Tabs, TextField, MenuItem,
    InputAdornment, CircularProgress, useTheme, useMediaQuery, Collapse,
    Button, TablePagination, FormControl, InputLabel, Select, Tooltip,
    Stack
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';
import api from '../services/api';

const TIPO_META = {
    CRIADO:   { label: 'Criado',   color: 'success', border: '#4caf50' },
    ALTERADO: { label: 'Alterado', color: 'warning', border: '#ed6c02' },
    EXCLUIDO: { label: 'Excluído', color: 'error',   border: '#d32f2f' },
};

function TipoAlteracaoChip({ tipo }) {
    const m = TIPO_META[tipo];
    if (!m) return <Chip label={tipo} size="small" />;
    return <Chip label={m.label} color={m.color} size="small" sx={{ fontWeight: 'bold' }} />;
}

function StatusChip({ status }) {
    switch (status) {
        case 'PAGO':      return <Chip label="Recebido" color="success" size="small" variant="outlined" />;
        case 'PENDENTE':  return <Chip label="Pendente" color="warning" size="small" variant="outlined" />;
        case 'VENCIDO':   return <Chip label="Vencido"  color="error"   size="small" variant="outlined" />;
        case 'PARCIAL':   return <Chip label="Parcial"  color="info"    size="small" variant="outlined" />;
        case 'CANCELADO': return <Chip label="Cancelado" color="default" size="small" variant="outlined" />;
        case 'ATIVO':     return <Chip label="Ativo"    color="success" size="small" variant="outlined" />;
        case 'INATIVO':   return <Chip label="Inativo"  color="default" size="small" variant="outlined" />;
        default:          return status ? <Chip label={status} size="small" variant="outlined" /> : null;
    }
}

const TABS = [
    { label: 'Contas a Receber', endpoint: '/historico-financeiro/contas-receber', statusOptions: ['PAGO','PENDENTE','VENCIDO','PARCIAL','CANCELADO'] },
    { label: 'Contas a Pagar',   endpoint: '/historico-financeiro/contas-pagar',   statusOptions: ['PAGO','PENDENTE','VENCIDO','PARCIAL','CANCELADO'] },
    { label: 'Conta Bancária',   endpoint: '/historico-financeiro/conta-bancaria', statusOptions: ['ATIVO','INATIVO'] },
];

const fmtBRL = v => v == null ? '—'
    : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtDate = v => v ? new Date(v).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—';

const fmtDateTime = v => v ? new Date(v).toLocaleString('pt-BR') : '—';

export default function HistoricoFinanceiro() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const location = useLocation();

    const initialTab = Math.min(
        parseInt(new URLSearchParams(location.search).get('tab') || '0'),
        2
    );

    const [tabAtual, setTabAtual] = useState(initialTab);
    const [content, setContent] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const [busca, setBusca] = useState('');
    const [debouncedBusca, setDebouncedBusca] = useState('');
    const [tipoAlteracao, setTipoAlteracao] = useState('');
    const [status, setStatus] = useState('');
    const [usuario, setUsuario] = useState('');
    const [debouncedUsuario, setDebouncedUsuario] = useState('');
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedBusca(busca), 300);
        return () => clearTimeout(t);
    }, [busca]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedUsuario(usuario), 300);
        return () => clearTimeout(t);
    }, [usuario]);

    const tab = TABS[tabAtual];
    const isBancaria = tabAtual === 2;

    const hasActiveFilters = !!(tipoAlteracao || status || debouncedUsuario || dataInicio || dataFim);

    const buildParams = useCallback(() => {
        const params = { page, size: rowsPerPage };
        if (debouncedBusca) params.busca = debouncedBusca;
        if (tipoAlteracao) params.tipoAlteracao = tipoAlteracao;
        if (status) params.status = status;
        if (debouncedUsuario) params.usuario = debouncedUsuario;
        if (dataInicio) params.dataInicio = dataInicio;
        if (dataFim) params.dataFim = dataFim;
        return params;
    }, [page, rowsPerPage, debouncedBusca, tipoAlteracao, status, debouncedUsuario, dataInicio, dataFim]);

    const carregarDados = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(tab.endpoint, { params: buildParams() });
            setContent(res.data?.content ?? []);
            setTotalElements(res.data?.totalElements ?? 0);
        } catch {
            setContent([]);
            setTotalElements(0);
        } finally {
            setLoading(false);
        }
    }, [tab.endpoint, buildParams]);

    useEffect(() => { carregarDados(); }, [carregarDados]);

    useEffect(() => { setPage(0); }, [debouncedBusca, tipoAlteracao, status, debouncedUsuario, dataInicio, dataFim, tabAtual]);

    const handleTabChange = (_, newTab) => {
        setTabAtual(newTab);
        setBusca(''); setTipoAlteracao(''); setStatus('');
        setUsuario(''); setDataInicio(''); setDataFim('');
        setPage(0);
    };

    const handleLimparFiltros = () => {
        setTipoAlteracao(''); setStatus('');
        setUsuario(''); setDataInicio(''); setDataFim('');
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <HistoryIcon sx={{ color: '#1ABB9C', fontSize: 32 }} />
                    <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold">
                        Histórico Financeiro
                    </Typography>
                </Box>
                <Tooltip title="Atualizar">
                    <IconButton onClick={carregarDados} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <Paper elevation={0} sx={{ bgcolor: '#fff', borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <Tabs
                    value={tabAtual}
                    onChange={handleTabChange}
                    variant={isMobile ? 'scrollable' : 'standard'}
                    scrollButtons="auto"
                    sx={{ borderBottom: '1px solid #e0e0e0', px: 2, pt: 1 }}
                >
                    {TABS.map((t, i) => (
                        <Tab key={i} label={t.label} sx={{ textTransform: 'none', fontWeight: 500 }} />
                    ))}
                </Tabs>

                <Box sx={{ p: isMobile ? 2 : 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                        <TextField
                            placeholder={isBancaria ? 'Buscar por banco...' : 'Buscar por descrição...'}
                            variant="outlined"
                            fullWidth
                            value={busca}
                            onChange={e => setBusca(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment>,
                                endAdornment: busca && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setBusca('')} edge="end"><ClearIcon /></IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Tooltip title="Filtros avançados">
                            <Box sx={{ position: 'relative' }}>
                                <IconButton
                                    onClick={() => setShowFilters(!showFilters)}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: showFilters || hasActiveFilters ? 'primary.main' : '#e0e0e0',
                                        borderRadius: 2, p: 1.2,
                                        bgcolor: showFilters || hasActiveFilters ? 'rgba(25,118,210,0.08)' : 'transparent',
                                        color: showFilters || hasActiveFilters ? 'primary.main' : 'text.secondary',
                                    }}
                                >
                                    {showFilters ? <FilterListOffIcon /> : <FilterListIcon />}
                                </IconButton>
                                {hasActiveFilters && !showFilters && (
                                    <Box sx={{ position: 'absolute', top: 4, right: 4, width: 8, height: 8, bgcolor: 'error.main', borderRadius: '50%' }} />
                                )}
                            </Box>
                        </Tooltip>
                    </Box>

                    <Collapse in={showFilters}>
                        <Box sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px dashed #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Operação</InputLabel>
                                <Select value={tipoAlteracao} label="Operação" onChange={e => setTipoAlteracao(e.target.value)} sx={{ bgcolor: '#fff' }}>
                                    <MenuItem value="">Todas</MenuItem>
                                    <MenuItem value="CRIADO">Criado</MenuItem>
                                    <MenuItem value="ALTERADO">Alterado</MenuItem>
                                    <MenuItem value="EXCLUIDO">Excluído</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel>Status</InputLabel>
                                <Select value={status} label="Status" onChange={e => setStatus(e.target.value)} sx={{ bgcolor: '#fff' }}>
                                    <MenuItem value="">Todos</MenuItem>
                                    {tab.statusOptions.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                </Select>
                            </FormControl>

                            <TextField
                                label="Usuário"
                                size="small"
                                value={usuario}
                                onChange={e => setUsuario(e.target.value)}
                                sx={{ minWidth: 160, bgcolor: '#fff' }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" sx={{ color: '#999' }} /></InputAdornment>,
                                }}
                            />

                            <TextField
                                label="De"
                                size="small"
                                type="date"
                                value={dataInicio}
                                onChange={e => setDataInicio(e.target.value)}
                                sx={{ minWidth: 160, bgcolor: '#fff' }}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="Até"
                                size="small"
                                type="date"
                                value={dataFim}
                                onChange={e => setDataFim(e.target.value)}
                                sx={{ minWidth: 160, bgcolor: '#fff' }}
                                InputLabelProps={{ shrink: true }}
                            />

                            {hasActiveFilters && (
                                <Button variant="text" color="error" size="small" onClick={handleLimparFiltros} sx={{ fontWeight: 'bold' }}>
                                    Limpar filtros
                                </Button>
                            )}
                        </Box>
                    </Collapse>

                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {totalElements} registro(s){(debouncedBusca || hasActiveFilters) && ' • Filtros aplicados'}
                        </Typography>
                    </Stack>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress sx={{ color: '#1ABB9C' }} />
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table size={isMobile ? 'small' : 'medium'}>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                        <TableCell sx={{ width: 6, p: 0 }} />
                                        <TableCell><strong>Descrição</strong></TableCell>
                                        <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                            <strong>{isBancaria ? 'Saldo' : 'Valor'}</strong>
                                        </TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                        {!isBancaria && (
                                            <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                                <strong>Vencimento</strong>
                                            </TableCell>
                                        )}
                                        <TableCell><strong>Operação</strong></TableCell>
                                        <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                            <strong>Data/Hora</strong>
                                        </TableCell>
                                        <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                            <strong>Por</strong>
                                        </TableCell>
                                        <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                            <strong>Rev.</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {content.length > 0 ? content.map((r, idx) => {
                                        const meta = TIPO_META[r.tipoAlteracao];
                                        return (
                                            <TableRow key={`${r.entidadeId}-${r.numeroRevisao}-${idx}`}
                                                sx={{ '&:hover': { bgcolor: '#fcfcfc' } }}>
                                                <TableCell sx={{ p: 0, borderLeft: `4px solid ${meta?.border || '#bdbdbd'}` }} />
                                                <TableCell sx={{ fontWeight: 500 }}>{r.descricao || '—'}</TableCell>
                                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell', fontVariantNumeric: 'tabular-nums' }}>
                                                    {fmtBRL(r.valor)}
                                                </TableCell>
                                                <TableCell><StatusChip status={r.status} /></TableCell>
                                                {!isBancaria && (
                                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                                        {fmtDate(r.dataVencimento)}
                                                    </TableCell>
                                                )}
                                                <TableCell><TipoAlteracaoChip tipo={r.tipoAlteracao} /></TableCell>
                                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {fmtDateTime(r.alteradoEm)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {r.alteradoPor || '—'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        #{r.numeroRevisao}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }) : (
                                        <TableRow>
                                            <TableCell colSpan={isBancaria ? 8 : 9} align="center" sx={{ py: 4 }}>
                                                <Typography color="text.secondary">
                                                    Nenhum registro encontrado.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    <TablePagination
                        component="div"
                        count={totalElements}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        labelRowsPerPage="Linhas por página"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    />
                </Box>
            </Paper>
        </Box>
    );
}
